/**
 * WSProvider — connect/disconnect the singleton ``WSClient`` to the
 * authenticated session.
 *
 * Mounted inside ``_authed/route.tsx`` so the socket is alive exactly while
 * the user is on a protected route and dies on logout. Status is exposed
 * via context so any descendant (notification pill, connectivity indicator)
 * can subscribe without re-running ``WSClient.onStatusChange`` plumbing.
 *
 * Resolution chain for the WS URL (port of v1's
 * ``ando-frontend/app/components/contexts/SocketContext.tsx::buildUrl``):
 *   1. ``getEnv().wsUrl`` if set (Docker single-image runtime config).
 *   2. Derive from ``getEnv().apiUrl`` by swapping ``http`` → ``ws`` and
 *      appending ``/ws/query_pydantic`` (the v1 default path).
 *
 * Device fingerprint header is forwarded to ``WSClient`` so the BE can
 * bind the socket to the same device that authenticated the cookie
 * (Zitadel-style); failure is best-effort, never blocks the connect.
 */

import { getDeviceId } from "@ando/core/auth";
import { getEnv } from "@ando/core/env";
import type { WSStatus } from "@ando/core/ws";
import * as React from "react";

import { getWsClient } from "./wsClientSingleton.js";

interface WSContextShape {
  status: WSStatus;
}

const WSContext = React.createContext<WSContextShape | null>(null);

/** Derive the WS URL. Cheap pure function — safe to call on every render. */
function resolveWsUrl(): string {
  const env = getEnv();
  if (env.wsUrl) return env.wsUrl;
  const apiUrl = env.apiUrl || "/api/v1";
  // ``apiUrl`` may be a relative path (``/api/v1``) — in that case anchor it
  // to the current origin so the swap to ``ws://`` produces a valid URL.
  const resolved =
    apiUrl.startsWith("http") ? apiUrl : `${window.location.origin}${apiUrl}`;
  const wsBase = resolved.replace(/^http/, "ws");
  return `${wsBase.replace(/\/$/, "")}/ws/query_pydantic`;
}

/**
 * Tauri-only token provider. The web build leaves it ``undefined`` so the
 * ``WSClient`` keeps the cookie-based path untouched. Dynamic import keeps
 * the desktop OAuth module out of the web bundle.
 */
const tokenProvider: (() => Promise<string | null>) | undefined = getEnv().isTauri
  ? async () => (await import("@/features/auth/desktop")).getDesktopAccessToken()
  : undefined;

export function WSProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [status, setStatus] = React.useState<WSStatus>("idle");

  React.useEffect(() => {
    const client = getWsClient();
    let cancelled = false;

    void (async () => {
      let deviceId: string | undefined;
      try {
        deviceId = (await getDeviceId()) || undefined;
      } catch {
        // Fingerprint best-effort — never block the connect.
      }
      if (cancelled) return;

      void client
        .connect({
          url: resolveWsUrl(),
          deviceId,
          getAccessToken: tokenProvider,
          onStatusChange: setStatus,
          onUnknownMessage: (raw, err) => {
            // Dev observability only — feature slices subscribe to typed
            // events via ``client.onMessage(type, handler)``.
            if (import.meta.env.DEV) {
              console.warn("[ws] unparseable frame", { raw, err });
            }
          },
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.warn("[ws] initial connect failed", err);
          }
        });
    })();

    return () => {
      cancelled = true;
      client.disconnect();
    };
  }, []);

  const value = React.useMemo<WSContextShape>(() => ({ status }), [status]);
  return <WSContext.Provider value={value}>{children}</WSContext.Provider>;
}

/** Read connection status from anywhere inside ``<WSProvider>``. */
export function useWsStatus(): WSStatus {
  const ctx = React.useContext(WSContext);
  if (!ctx) throw new Error("useWsStatus must be used inside <WSProvider>");
  return ctx.status;
}

/** Get the singleton ``WSClient`` — same instance as ``getWsClient()``. */
export function useWsClient() {
  return getWsClient();
}
